/**
 * BundleBuilder-local RHF fork of TargetingForm.tsx + CommonFields/Additional.tsx
 * (shared across bundle types — not editable in place). Mirrors
 * VolumeDiscount/VolumeTargetForm/VolumeTargetingForm.tsx.
 *
 * For BUNDLE_BUILDER specifically, Additional.tsx's "Widget Position" section
 * is explicitly excluded (`![...BUNDLE_BUILDER].includes(bundleType)`) since
 * BundleBuilder has its own Bundle Visibility/embed UI in
 * BundleBuilderAdvanced.tsx, and its "Product Settings" tabs list has no
 * entry for BUNDLE_BUILDER either — so only Scheduling (skip-key) and
 * Customer Targeting (skip-key) actually render for this bundle type.
 */
import { useContext, useState } from "react";
import styles from "../BundleBuilder.module.css";
import { Collapsible, Divider, FormLayout, Label } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { useFormContext, useWatch, useController } from "react-hook-form";
import AutoComplete from "../../../../components/AutoComplete";
import TagsField from "../../../../components/TagsField";
import { countryArray } from "../../../../utilities/constants";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { currencyToSymbols, TCurrencyCode } from "../../../utilities";
import useBundleMixpanelHook from "../../../../hooks/useBundleMixpanelHook";
import { ActiveDatesCard } from "@shopify/discount-app-components";
import AndSeparator from "../../../../components/Separators/AndSeparator";
import { BundleTypes } from "../../constants";
import {
    FormCheckbox,
    FormTextField,
    FormSelect,
    FormRadioButton,
    useVariantFieldDisabled,
} from "../../../../components/FormFields";

interface AppConfigContextType {
    shop: string;
    extensionId: string;
    currency: string;
    iana_timezone?: string;
    [key: string]: any;
}

const BundleBuilderTargetingForm = () => {
    const { control, setValue, getValues } = useFormContext();
    const appConfigContext = useContext(AppConfigContext) as AppConfigContextType;

    const start_date = useWatch({ control, name: "start_date" });
    const end_date = useWatch({ control, name: "end_date" });
    const targetingTypeValue = useWatch({ control, name: "targeting.targetingType" }) ?? "all";

    const isScheduleAbDisabled = useVariantFieldDisabled("start_date");
    const isTargetingAbDisabled = useVariantFieldDisabled("targeting");

    const [expandSchedule, setExpandSchedule] = useState(false);
    const [expandCustomerTargeting, setExpandCustomerTargeting] = useState(false);

    const { trackFormSections, trackTargeting } = useBundleMixpanelHook();

    const { field: customerCountriesField } = useController({
        name: "targeting.customerCountries",
        control,
        defaultValue: [],
    });
    const { field: customerTagsField } = useController({ name: "targeting.customerTags", control, defaultValue: [] });

    const handleResetTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "All",
            bundleType: BundleTypes.BUNDLE_BUILDER,
        });
        setValue(
            "targeting",
            {
                ...getValues("targeting"),
                includeCustomers: "all",
                excludeCustomers: "none",
                includeCustomerData: "",
                excludeCustomerData: "",
                shopifyMarketsTargeting: "all",
                includeLocations: [],
                excludeLocations: [],
                shopifyMarketsTargetingData: [],
                targetingType: "all",
            },
            { shouldDirty: true },
        );
    };

    const handleSpecificTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "Specific",
            bundleType: BundleTypes.BUNDLE_BUILDER,
        });
        setValue("targeting.targetingType", "specific", { shouldDirty: true });
    };

    const sectionArrow = (open: boolean) => (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transform: open ? "rotate(90deg)" : "rotate(270deg)",
            }}
        >
            <MdOutlineArrowBackIosNew size={14} />
        </div>
    );

    return (
        <div className={styles.collapsibleFormBox}>
            {/* ── Scheduling (skip-key) ───────────────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandSchedule((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Additional ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.BUNDLE_BUILDER,
                        });
                        return !ps;
                    })
                }
            >
                <div>Scheduling</div>
                {sectionArrow(expandSchedule)}
            </div>
            <Collapsible open={expandSchedule} id="scheduling-collapsible">
                <FormLayout>
                    <div style={{ marginBottom: "-1rem" }}>
                        {appConfigContext?.iana_timezone && (
                            <ActiveDatesCard
                                startDate={{
                                    value: start_date,
                                    onChange: (val: string) => setValue("start_date", val, { shouldDirty: true }),
                                }}
                                endDate={{
                                    value: end_date,
                                    onChange: (val: string | null) => setValue("end_date", val, { shouldDirty: true }),
                                }}
                                timezoneAbbreviation={appConfigContext.iana_timezone}
                                disabled={isScheduleAbDisabled}
                            />
                        )}
                    </div>
                </FormLayout>
            </Collapsible>

            <div className={styles.dividerStyle}>
                <Divider borderColor="border" />
            </div>

            {/* ── Customer Targeting (skip-key) ───────────────────────────────── */}
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandCustomerTargeting((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Customer Targeting ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: BundleTypes.BUNDLE_BUILDER,
                        });
                        return !ps;
                    })
                }
            >
                <div>Customer Targeting</div>
                {sectionArrow(expandCustomerTargeting)}
            </div>
            <Collapsible open={expandCustomerTargeting} id="customer-targeting-collapsible">
                <FormLayout>
                    <div className={styles.customLabelWrapper}>
                        <Label id="targeting_label">Display Bundle to:</Label>
                    </div>
                    <div className={styles.formCol}>
                        <FormRadioButton
                            name="targeting.targetingType"
                            value="all"
                            label="All Customers"
                            onChangeHook={handleResetTargeting}
                        />
                        <FormRadioButton
                            name="targeting.targetingType"
                            value="specific"
                            label="Specific Customer Segment"
                            onChangeHook={handleSpecificTargeting}
                        />

                        {targetingTypeValue === "specific" && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "80%",
                                    marginLeft: 25,
                                }}
                            >
                                <div>
                                    <FormCheckbox
                                        name="targeting.customerTagsEnabled"
                                        label="Based on customer tags"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value: "Target customers based on customer tag",
                                                bundleType: BundleTypes.BUNDLE_BUILDER,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerTagType"
                                            label="Exclusion/Inclusion"
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                        />
                                        <TagsField
                                            label="Customer tags"
                                            bundleType={BundleTypes.BUNDLE_BUILDER}
                                            section="Display bar to"
                                            labelHidden
                                            placeholder="Add tags"
                                            value={customerTagsField.value}
                                            onChange={(val) =>
                                                setValue("targeting.customerTags", val, { shouldDirty: true })
                                            }
                                            autoComplete="off"
                                            disabled={isTargetingAbDisabled}
                                        />
                                    </div>
                                </div>

                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />

                                <div>
                                    <FormCheckbox
                                        name="targeting.customerCountriesEnabled"
                                        label="Based on customer countries"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value: "Target customers based on country",
                                                bundleType: BundleTypes.BUNDLE_BUILDER,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerCountryType"
                                            label="Exclusion/Inclusion"
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                        />
                                        <AutoComplete
                                            label="Based on customer countries"
                                            labelHidden
                                            optionsArray={countryArray.map(
                                                (country: { symbol: string; name: string }) => ({
                                                    value: country.symbol,
                                                    label: country.name,
                                                }),
                                            )}
                                            selected={customerCountriesField.value}
                                            onChange={(val) =>
                                                setValue("targeting.customerCountries", val, { shouldDirty: true })
                                            }
                                            placeholder="Countries"
                                            listLabel="Countries to include"
                                            disabled={isTargetingAbDisabled}
                                        />
                                    </div>
                                </div>

                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />

                                <div>
                                    <FormCheckbox
                                        name="targeting.customerSpentEnabled"
                                        label="Based on minimum lifetime spent"
                                        onChangeSideEffect={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Hide Bundle from",
                                                value: "Minimum amount spent in store",
                                                bundleType: BundleTypes.BUNDLE_BUILDER,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <FormSelect
                                            name="targeting.customerSpentType"
                                            label="Comparison"
                                            labelHidden
                                            options={[
                                                { label: "Greater than", value: "greater_than" },
                                                { label: "Less than", value: "less_than" },
                                            ]}
                                        />
                                        <FormTextField
                                            name="targeting.customerSpent"
                                            label="Spent amount"
                                            labelHidden
                                            placeholder="00"
                                            type="number"
                                            prefix={currencyToSymbols[appConfigContext.currency as TCurrencyCode]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </FormLayout>
            </Collapsible>
        </div>
    );
};

export default BundleBuilderTargetingForm;
